import React from 'react';
import debounce from 'lodash/debounce';

export default class Form extends React.Component {
  /**
   *  Getting the state from parent or else
   *  send the current state from (this.state)
   */

  getFormState = () => {
    if (this.props.getFormState) {
      return this.props.getFormState();
    } else {
      return this.state;
    }
  };

  /**
   *  Setting the state to the callee component and
   *  executing the callback method (done)
   */

  setFormState = (state = {}, done) => {
    if (this.props.setFormState) {
      this.props.setFormState({ ...state, defaultState: this.getFormDefaultState() }, done);
    } else {
      this.setState(state, done);
    }
  };

  /**
   *  Get the form values from parent component
   *  if not available call local method getFormState
   */

  getFormValues = () => {
    let formValues;
    if (this.props.getFormValues) {
      formValues = this.props.getFormValues() || {};
    }
    if (!formValues) {
      const formState = this.getFormState() || {};
      formValues = formState.formValues || {};
    }
    return formValues;
  };

  /**
   * Submit button
   */

  submitForm = (extraParams) => {
    const formValues = this.getFormValues();
    this.props.onSubmit(formValues, extraParams);
  };

  /**
   *  Setting default state of this component and this state also get
   *  propagated to callee component if the component is overriden the
   *  local method setFormState()
   */

  getFormDefaultState() {
    const { defaultValues = {} } = this.props;
    return {
      formValues: { ...defaultValues },
      prevFormValues: { ...defaultValues },
      isTouched: false,
      formError: {},
      currAction: ''
    };
  }

  constructor(props) {
    super(props);
    const { defaultValues = {} } = props;
    if (props.autoSubmitInterval != null) {
      this.autoSubmit = debounce(this.submitForm, props.autoSubmitInterval || 0);
    }
    this.state = {
      ...this.getFormDefaultState(),
      defaultState: this.getFormDefaultState()
    };
  }

  /**
   * getting the value of provided (name) parameter
   * from formValues
   */
  getFormValue = name => {
    const formValues = this.getFormValues();
    return formValues[name] || '';
  };

  getFormDefaultProps = ({ type, name, value }) => {
    if (type === 'text') {
      return {
        onChange: event => {
          const { name, value } = event.target;
          this.addFormValue(name, value);
        },
        name,
        type,
        value: this.getFormValue(name)
      };
    } else if (type === 'radio') {
      return {
        onChange: event => {
          const { name, value, checked } = event.target;
          const items = this.getFormValue(name);
          if (items.includes(value)) {
            this.addFormValue(name, []);
          } else {
            this.addFormValue(name, [value]);
          }
        },
        checked: this.getFormValue(name).length === 1,
        name,
        type,
        value
      };
    } else if (type === 'number') {
      return {
        onChange: event => {
          const { name, value } = event.target;
          this.addFormValue(name, value);
        },
        name,
        type,
        value: this.getFormValue(name)
      };
    } else if (type === 'checkbox') {
      return {
        onChange: event => {
          const { name, value, checked } = event.target;
          const items = this.getFormValue(name);
          if (!items) {
            this.addFormValue(name, [value]);
          } else if (items.includes(value)) {
            const i_value = items.indexOf(value);
            if (i_value > -1) {
              items.splice(i_value, 1);
            }
            this.addFormValue(name, items);
          } else if (!items.includes(value)) {
            items.push(value);
            this.addFormValue(name, items);
          }
        },
        onKeyDown: event => {
          if (event.key === 'Enter') {
            //Prevent form submission
            event.preventDefault();

            //Toggle checkbox manually
            const { name, value } = event.target;
            const items = this.getFormValue(name) || [];

            if (!items) {
              this.addFormValue(name, [value]);
            } else if (items.includes(value)) {
              const i_value = items.indexOf(value);
              if (i_value > -1) {
                items.splice(i_value, 1);
              }
              this.addFormValue(name, items);
            } else if (!items.includes(value)) {
              items.push(value);
              this.addFormValue(name, items);
            }
          }
        },
        checked: this.getFormValue(name) ? this.getFormValue(name).includes(value) : false,
        name,
        type,
        value
      };
    }
  };

  /**
   *  Adding data inside formValues from callee component and maintaining the
   *  formdata locally and later invoking setFormState method. to update the state
   *  of callee component with invoked (value) for (name) inside formValues
   */
  addFormValue = (name, value) => {
    const formValues = this.getFormValues();
    const { autoSubmitInterval, setFormValues } = this.props;
    const prevFormValues = { ...formValues };
    formValues[name] = value;

    this.setFormState(
      {
        formValues,
        prevFormValues,
        isTouched: true,
        currAction: 'inputChange'
      },
      () => {
        if (setFormValues) {
          setFormValues(formValues);
        }
        if (autoSubmitInterval != null) {
          this.autoSubmit();
        }
      }
    );
  };

  /**
   *  Validates and setting formError so that it should be visible to callee component
   *  if setFormState is overriden & error is there it'll re-render the callee component based on logic
   */

validateForm = () => {
  const formValues = this.getFormValues();

  if (this.props.validateForm) {
    const formError = this.props.validateForm(formValues);

    this.setFormState({ formError }, () => {
      // MOVE FOCUS to error field AFTER ERROR IS SET
      if (formError && Object.keys(formError).length > 0) {
        if (this.props.onValidationError) {
          this.props.onValidationError(formError);
        }
      }
    });

    if (formError && Object.keys(formError).length > 0) {
      return false;
    } else {
      return true;
    }
  }
  return true;
};


  handleSubmit = event => {
    event.preventDefault();
    this.setFormState({ currAction: 'submit' });
    if (this.validateForm()) {
      this.submitForm();
    }
  };

  resetAll = () => {
    const defaultState = this.getFormDefaultState() || {};
    if (this.props.setFormValues) {
      this.props.setFormValues(defaultState.formValues);
    }
    this.setFormState({ ...defaultState });
  };

  render() {
    const { prevFormValues = {}, isTouched, formError = {}, currAction } = this.getFormState();
    const { role } = this.props;
    return (
      <form onSubmit={this.handleSubmit} {...(role ? {role} : {} )} >
        {this.props.children({
          formValues: this.getFormValues(),
          prevFormValues,
          getFormDefaultProps: this.getFormDefaultProps,
          addFormValue: this.addFormValue,
          getFormValue: this.getFormValue,
          isTouched,
          formError,
          validateForm: this.validateForm,
          currAction,
          resetAll: this.resetAll
        })}
      </form>
    );
  }
}
